import i18next, { i18n } from "i18next";

import { COMMON_NAMESPACE, DEFAULT_LOCALE, LOCALES } from "../locales";
import { ICreateI18nParams } from "./create-i18n.models";
import { toI18nextResources } from "./create-i18n.utils";

/**
 * Builds an isolated, already-initialised i18next instance.
 *
 * Always a fresh instance rather than the `i18next` singleton: the landing's
 * prerender renders every locale inside one Node process, and a shared instance
 * would leak the last language into the next page. Consumers hand it to
 * `<I18nextProvider>`; nothing here touches React.
 */
export const createI18n = ({
  locale,
  bundles,
  defaultNS = COMMON_NAMESPACE,
  plugins = [],
  options = {},
}: ICreateI18nParams): i18n => {
  const instance = i18next.createInstance();

  for (const plugin of plugins) {
    instance.use(plugin);
  }

  instance.init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: LOCALES,
    // "en-GB" from a browser or a detector must resolve to the "en" catalogue.
    nonExplicitSupportedLngs: true,
    ns: Object.keys(bundles),
    defaultNS,
    resources: toI18nextResources(bundles),
    // React already escapes everything it renders.
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    ...options,
  });

  return instance;
};
